import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, FileCheck, CreditCard, AlertCircle, Building2, Phone, Wrench, FileText, Award, AlertTriangle, MessageSquareWarning } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, logout } = useAuth();

  // Check session validity on dashboard load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await api.get('/auth/check-session');
        if (!response.data.valid) {
          toast.error(response.data.reason || 'Session expired. Please login again.');
          logout();
        }
      } catch (error) {
        // If check fails due to auth error, logout
        if (error.response?.status === 401) {
          logout();
        }
      }
    };
    
    if (user) {
      checkSession();
    }
  }, [user, logout]);

  // Redirect Service Engineers to their dedicated dashboard
  if (user?.role === 'Service Engineer') {
    return <Navigate to="/engineer-dashboard" replace />;
  }

  // Redirect Sales & Marketing to their dedicated dashboard
  if (user?.role === 'Sales & Marketing') {
    return <Navigate to="/sales-dashboard" replace />;
  }

  const getDashboardContent = () => {
    switch (user?.role) {
      case 'Admin':
        return <AdminDashboard />;
      case 'Back Office Executive':
        return <BackOfficeDashboard />;
      case 'Backoffice Accounts Executive':
        return <BackOfficeAccountsDashboard />;
      default:
        return <div>Welcome to SUNTECH ENTERPRISES, Coimbatore</div>;
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome back, {user?.name}!</p>
      </div>
      {getDashboardContent()}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    employees: 0,
    customers: 0,
    leads: 0,
    newLeads: 0,
    services: 0,
    newServices: 0,
    pendingServices: 0,
    payments: 0,
    pendingPayments: 0,
    pendingPaymentsValue: 0,
    contracts: 0,
    activeContracts: 0,
    licenses: 0,
    expiringLicenses: 0,
    expiringContracts: 0,
    upcomingMMC: 0,
    newComplaints: 0
  });

  // Helper function to check if MMC service day is within next N days
  const isMMCServiceWithinDays = useCallback((serviceDay, days) => {
    if (!serviceDay) return false;
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    let nextServiceDate;
    if (serviceDay >= currentDay) {
      nextServiceDate = new Date(currentYear, currentMonth, serviceDay);
    } else {
      nextServiceDate = new Date(currentYear, currentMonth + 1, serviceDay);
    }
    
    const daysUntilService = Math.ceil((nextServiceDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilService >= 0 && daysUntilService <= days;
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [employeesRes, customersRes, leadsRes, contractsRes, licensesRes, salesStatsRes, complaintsStatsRes, servicesRes] = await Promise.all([
        api.get('/employees/'),
        api.get('/customers/'),
        api.get('/leads/'),
        api.get('/contracts/'),
        api.get('/licenses/'),
        api.get('/sales-billing/stats').catch(() => ({ data: { total_pending_count: 0, total_pending_amount: 0 } })),
        api.get('/complaints/stats'),
        api.get('/services/')
      ]);

      const newLeadsCount = leadsRes.data.filter(l => l.lead_status === 'New').length;
      const activeContractsCount = contractsRes.data.filter(c => c.contract_status === 'Active').length;
      
      // Count new and pending services
      const newServicesCount = servicesRes.data.filter(s => s.status === 'New').length;
      const pendingServicesCount = servicesRes.data.filter(s => s.status === 'New' || s.status === 'On Process').length;
      
      const today = new Date();
      const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      // Count licenses expiring in next 30 days
      const expiringLicensesCount = licensesRes.data.filter(l => {
        const renewalDate = new Date(l.renewal_due_date);
        return renewalDate <= thirtyDaysLater && renewalDate >= today && l.status === 'Active';
      }).length;

      // Count contracts (AMC) expiring in next 30 days
      const expiringContractsCount = contractsRes.data.filter(c => {
        if (c.contract_type !== 'AMC' || !c.end_date) return false;
        const endDate = new Date(c.end_date);
        return endDate <= thirtyDaysLater && endDate >= today && c.contract_status === 'Active';
      }).length;

      // Count MMC contracts with service due in next 7 days
      const upcomingMMCCount = contractsRes.data.filter(c => {
        return c.contract_type === 'MMC' && 
               c.contract_status === 'Active' && 
               isMMCServiceWithinDays(c.monthly_service_day, 7);
      }).length;

      setStats({
        employees: employeesRes.data.length,
        customers: customersRes.data.length,
        leads: leadsRes.data.length,
        newLeads: newLeadsCount,
        services: servicesRes.data.length,
        newServices: newServicesCount,
        pendingServices: pendingServicesCount,
        payments: (salesStatsRes.data.completed_payments_count || 0) + (salesStatsRes.data.pending_payments_count || 0),
        pendingPayments: salesStatsRes.data.pending_payments_count || 0,
        pendingPaymentsValue: salesStatsRes.data.pending_payments_total || 0,
        overduePayments: salesStatsRes.data.overdue_count || 0,
        contracts: contractsRes.data.length,
        activeContracts: activeContractsCount,
        licenses: licensesRes.data.length,
        expiringLicenses: expiringLicensesCount,
        expiringContracts: expiringContractsCount,
        upcomingMMC: upcomingMMCCount,
        newComplaints: complaintsStatsRes.data.new || 0
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, [isMMCServiceWithinDays]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statCards = [
    { 
      title: 'Total Employees', 
      value: stats.employees, 
      icon: Users, 
      color: 'bg-blue-500',
      link: '/employees'
    },
    { 
      title: 'Total Customers', 
      value: stats.customers, 
      icon: Building2, 
      color: 'bg-purple-500',
      link: '/customers'
    },
    { 
      title: 'Total Leads', 
      value: stats.leads, 
      icon: Phone, 
      color: 'bg-indigo-500',
      link: '/leads'
    },
    { 
      title: 'New Leads', 
      value: stats.newLeads, 
      icon: AlertCircle, 
      color: 'bg-orange-500',
      link: '/leads'
    },
    { 
      title: 'New Complaints', 
      value: stats.newComplaints, 
      icon: MessageSquareWarning, 
      color: stats.newComplaints > 0 ? 'bg-red-500' : 'bg-gray-500',
      link: '/complaints'
    },
    { 
      title: 'Active Contracts', 
      value: stats.activeContracts, 
      subtitle: `${stats.contracts} Total`,
      icon: FileText, 
      color: 'bg-teal-500',
      link: '/contracts'
    },
    { 
      title: 'Stamping/License', 
      value: stats.licenses, 
      subtitle: stats.expiringLicenses > 0 ? `${stats.expiringLicenses} Renewal Due` : 'All Good',
      icon: Award, 
      color: stats.expiringLicenses > 0 ? 'bg-amber-500' : 'bg-green-500',
      link: '/licenses'
    },
    { 
      title: 'New Service Requests', 
      value: stats.newServices, 
      subtitle: `${stats.services} Total Services`,
      icon: FileCheck, 
      color: stats.newServices > 0 ? 'bg-cyan-500' : 'bg-gray-500',
      link: '/services'
    },
    { 
      title: 'Pending Services', 
      value: stats.pendingServices, 
      subtitle: 'New + On Process',
      icon: Wrench, 
      color: stats.pendingServices > 0 ? 'bg-yellow-500' : 'bg-gray-500',
      link: '/services'
    },
    { 
      title: 'Pending Payments', 
      value: stats.pendingPayments, 
      subtitle: stats.pendingPaymentsValue > 0 ? `₹${stats.pendingPaymentsValue.toLocaleString('en-IN')}` : '₹0',
      icon: CreditCard, 
      color: stats.pendingPayments > 0 ? 'bg-red-500' : 'bg-gray-500',
      link: '/sales'
    },
  ];

  // Alert cards for expiring/upcoming items
  const alertCards = [];
  
  if (stats.expiringContracts > 0) {
    alertCards.push({
      title: 'AMC Contracts Expiring',
      value: stats.expiringContracts,
      subtitle: 'In Next 30 Days',
      icon: AlertTriangle,
      link: '/contracts?type=AMC&renewal=30',
      colorScheme: 'red'
    });
  }

  if (stats.upcomingMMC > 0) {
    alertCards.push({
      title: 'Upcoming MMC Services',
      value: stats.upcomingMMC,
      subtitle: 'In Next 7 Days',
      icon: Wrench,
      link: '/contracts?type=MMC&renewal=7',
      colorScheme: 'purple'
    });
  }
  
  if (stats.expiringLicenses > 0) {
    alertCards.push({
      title: 'Stamping/License Renewal Due',
      value: stats.expiringLicenses,
      subtitle: 'In Next 30 Days',
      icon: Award,
      link: '/licenses?renewal=30',
      colorScheme: 'amber'
    });
  }

  // Color scheme mappings for alert cards
  const getAlertColors = (scheme) => {
    const colors = {
      red: {
        border: 'border-red-200',
        bg: 'bg-gradient-to-r from-red-50 to-orange-50',
        title: 'text-red-700',
        value: 'text-red-600',
        subtitle: 'text-red-500',
        iconBg: 'bg-red-100',
        icon: 'text-red-600'
      },
      purple: {
        border: 'border-purple-200',
        bg: 'bg-gradient-to-r from-purple-50 to-indigo-50',
        title: 'text-purple-700',
        value: 'text-purple-600',
        subtitle: 'text-purple-500',
        iconBg: 'bg-purple-100',
        icon: 'text-purple-600'
      },
      amber: {
        border: 'border-amber-200',
        bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',
        title: 'text-amber-700',
        value: 'text-amber-600',
        subtitle: 'text-amber-500',
        iconBg: 'bg-amber-100',
        icon: 'text-amber-600'
      }
    };
    return colors[scheme] || colors.red;
  };

  return (
    <div className="space-y-6">
      {/* Alert Cards Section */}
      {alertCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {alertCards.map((alert, index) => {
            const Icon = alert.icon;
            const colors = getAlertColors(alert.colorScheme);
            return (
              <Card 
                key={index}
                className={`cursor-pointer ${colors.border} ${colors.bg} hover:shadow-lg transition-all duration-300`}
                onClick={() => navigate(alert.link)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${colors.title}`}>{alert.title}</p>
                      <p className={`text-3xl font-bold ${colors.value} mt-1`}>{alert.value}</p>
                      <p className={`text-xs ${colors.subtitle} mt-1`}>{alert.subtitle}</p>
                    </div>
                    <div className={`${colors.iconBg} p-3 rounded-full animate-bounce`}>
                      <Icon className={colors.icon} size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index} 
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(stat.link)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{stat.value}</p>
                    {stat.subtitle && (
                      <p className="text-sm font-semibold text-green-600 mt-1">{stat.subtitle}</p>
                    )}
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <Icon className="text-white" size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Manage your business operations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <button 
                onClick={() => navigate('/employees')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • Manage Employees
              </button>
              <button 
                onClick={() => navigate('/customers')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • Add New Customers
              </button>
              <button 
                onClick={() => navigate('/leads')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • Create Leads
              </button>
              <button 
                onClick={() => navigate('/contracts')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • Manage Contracts (AMC/MMC)
              </button>
              <button 
                onClick={() => navigate('/licenses')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • Manage Stamping/Licenses
              </button>
              <button 
                onClick={() => navigate('/reports')}
                className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
              >
                • View Reports
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
            <CardDescription>All systems operational</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Employee Management</span>
                <span className="text-sm text-green-600 font-medium">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Customer Management</span>
                <span className="text-sm text-green-600 font-medium">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Leads & Followups</span>
                <span className="text-sm text-green-600 font-medium">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Contracts (AMC/MMC)</span>
                <span className="text-sm text-green-600 font-medium">✓ Active</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Stamping/License Management</span>
                <span className="text-sm text-green-600 font-medium">✓ Active</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const BackOfficeDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    customers: 0,
    leads: 0,
    newLeads: 0,
    services: 0,
    newServices: 0,
    pendingServices: 0,
    pendingApprovalServices: 0,
    newComplaints: 0,
    totalComplaints: 0,
    pendingPayments: 0,
    pendingPaymentsValue: 0,
    expiringContracts: 0,
    expiringLicenses: 0,
    upcomingMMC: 0
  });

  const fetchDashboardData = async () => {
    try {
      const [customersRes, leadsRes, contractsRes, licensesRes, servicesRes, complaintsRes, salesStatsRes] = await Promise.all([
        api.get('/customers/'),
        api.get('/leads/'),
        api.get('/contracts/'),
        api.get('/licenses/'),
        api.get('/services/'),
        api.get('/complaints/stats'),
        api.get('/sales-billing/stats').catch(() => ({ data: { pending_payments_count: 0, pending_payments_total: 0 } }))
      ]);

      const newLeadsCount = leadsRes.data.filter(l => l.lead_status === 'New').length;
      
      // Service stats
      const newServicesCount = servicesRes.data.filter(s => s.status === 'New').length;
      const pendingServicesCount = servicesRes.data.filter(s => s.status === 'New' || s.status === 'On Process').length;
      const pendingApprovalCount = servicesRes.data.filter(s => s.status === 'Closed' && s.approval_status === 'Pending').length;

      const today = new Date();
      const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      // AMC Contracts expiring in next 30 days
      const expiringContractsCount = contractsRes.data.filter(c => {
        if (c.contract_type !== 'AMC' || !c.end_date) return false;
        const endDate = new Date(c.end_date);
        return endDate <= thirtyDaysLater && endDate >= today && c.contract_status === 'Active';
      }).length;

      // Licenses expiring in next 30 days
      const expiringLicensesCount = licensesRes.data.filter(l => {
        const renewalDate = new Date(l.renewal_due_date);
        return renewalDate <= thirtyDaysLater && renewalDate >= today && l.status === 'Active';
      }).length;

      // MMC contracts with service due in next 7 days
      const upcomingMMCCount = contractsRes.data.filter(c => {
        return c.contract_type === 'MMC' && 
               c.contract_status === 'Active' && 
               isMMCServiceWithinDays(c.monthly_service_day, 7);
      }).length;

      setStats({
        customers: customersRes.data.length,
        leads: leadsRes.data.length,
        newLeads: newLeadsCount,
        services: servicesRes.data.length,
        newServices: newServicesCount,
        pendingServices: pendingServicesCount,
        pendingApprovalServices: pendingApprovalCount,
        newComplaints: complaintsRes.data.new || 0,
        totalComplaints: (complaintsRes.data.new || 0) + (complaintsRes.data.in_progress || 0) + (complaintsRes.data.resolved || 0),
        pendingPayments: salesStatsRes.data.pending_payments_count || 0,
        pendingPaymentsValue: salesStatsRes.data.pending_payments_total || 0,
        expiringContracts: expiringContractsCount,
        expiringLicenses: expiringLicensesCount,
        upcomingMMC: upcomingMMCCount
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Helper function to check if MMC service day is within next N days
  const isMMCServiceWithinDays = (serviceDay, days) => {
    if (!serviceDay) return false;
    const today = new Date();
    const currentDay = today.getDate();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    let nextServiceDate;
    if (serviceDay >= currentDay) {
      nextServiceDate = new Date(currentYear, currentMonth, serviceDay);
    } else {
      nextServiceDate = new Date(currentYear, currentMonth + 1, serviceDay);
    }
    
    const daysUntilService = Math.ceil((nextServiceDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilService >= 0 && daysUntilService <= days;
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Alert cards for expiring items
  const alertCards = [];
  
  if (stats.expiringContracts > 0) {
    alertCards.push({
      title: 'AMC Contracts Expiring',
      value: stats.expiringContracts,
      subtitle: 'In Next 30 Days',
      icon: AlertTriangle,
      link: '/contracts?type=AMC&renewal=30',
      colorScheme: 'red'
    });
  }

  if (stats.upcomingMMC > 0) {
    alertCards.push({
      title: 'Upcoming MMC Services',
      value: stats.upcomingMMC,
      subtitle: 'In Next 7 Days',
      icon: Wrench,
      link: '/contracts?type=MMC&renewal=7',
      colorScheme: 'purple'
    });
  }
  
  if (stats.expiringLicenses > 0) {
    alertCards.push({
      title: 'Stamping/License Renewal Due',
      value: stats.expiringLicenses,
      subtitle: 'In Next 30 Days',
      icon: Award,
      link: '/licenses?renewal=30',
      colorScheme: 'amber'
    });
  }

  // Color scheme mappings for alert cards
  const getAlertColors = (scheme) => {
    const colors = {
      red: {
        border: 'border-red-200',
        bg: 'bg-gradient-to-r from-red-50 to-orange-50',
        title: 'text-red-700',
        value: 'text-red-600',
        subtitle: 'text-red-500',
        iconBg: 'bg-red-100',
        icon: 'text-red-600'
      },
      purple: {
        border: 'border-purple-200',
        bg: 'bg-gradient-to-r from-purple-50 to-indigo-50',
        title: 'text-purple-700',
        value: 'text-purple-600',
        subtitle: 'text-purple-500',
        iconBg: 'bg-purple-100',
        icon: 'text-purple-600'
      },
      amber: {
        border: 'border-amber-200',
        bg: 'bg-gradient-to-r from-amber-50 to-yellow-50',
        title: 'text-amber-700',
        value: 'text-amber-600',
        subtitle: 'text-amber-500',
        iconBg: 'bg-amber-100',
        icon: 'text-amber-600'
      }
    };
    return colors[scheme] || colors.red;
  };

  return (
    <div className="space-y-6">
      {/* Alert Cards Section */}
      {alertCards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {alertCards.map((alert, index) => {
            const Icon = alert.icon;
            const colors = getAlertColors(alert.colorScheme);
            return (
              <Card 
                key={index}
                className={`cursor-pointer ${colors.border} ${colors.bg} hover:shadow-lg transition-all duration-300`}
                onClick={() => navigate(alert.link)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${colors.title}`}>{alert.title}</p>
                      <p className={`text-3xl font-bold ${colors.value} mt-1`}>{alert.value}</p>
                      <p className={`text-xs ${colors.subtitle} mt-1`}>{alert.subtitle}</p>
                    </div>
                    <div className={`${colors.iconBg} p-3 rounded-full animate-bounce`}>
                      <Icon className={colors.icon} size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/customers')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.customers}</p>
              </div>
              <div className="bg-blue-500 p-2 rounded-lg">
                <Building2 className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/leads')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Leads</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats.newLeads}</p>
                <p className="text-xs text-gray-500">{stats.leads} Total</p>
              </div>
              <div className="bg-orange-500 p-2 rounded-lg">
                <Phone className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/complaints')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Complaints</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.newComplaints}</p>
                <p className="text-xs text-gray-500">{stats.totalComplaints} Total</p>
              </div>
              <div className={`${stats.newComplaints > 0 ? 'bg-red-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <MessageSquareWarning className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/services')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Services</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{stats.pendingServices}</p>
                <p className="text-xs text-gray-500">New + On Process</p>
              </div>
              <div className={`${stats.pendingServices > 0 ? 'bg-yellow-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <Wrench className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/services?tab=pending-approval')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approval</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.pendingApprovalServices}</p>
                <p className="text-xs text-gray-500">Services to Approve</p>
              </div>
              <div className={`${stats.pendingApprovalServices > 0 ? 'bg-purple-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <FileCheck className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/sales')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.pendingPayments}</p>
                <p className="text-xs text-gray-500">₹{stats.pendingPaymentsValue.toLocaleString('en-IN')}</p>
              </div>
              <div className={`${stats.pendingPayments > 0 ? 'bg-red-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <CreditCard className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/services')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Services</p>
                <p className="text-2xl font-bold text-cyan-600 mt-1">{stats.newServices}</p>
                <p className="text-xs text-gray-500">{stats.services} Total</p>
              </div>
              <div className={`${stats.newServices > 0 ? 'bg-cyan-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <FileCheck className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/contracts')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Expiring AMC</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.expiringContracts}</p>
                <p className="text-xs text-gray-500">Next 30 Days</p>
              </div>
              <div className={`${stats.expiringContracts > 0 ? 'bg-amber-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <FileText className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Responsibilities</CardTitle>
          <CardDescription>CRM and service management activities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/customers')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage customer relationships
            </button>
            <button 
              onClick={() => navigate('/leads')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Handle leads and followups
            </button>
            <button 
              onClick={() => navigate('/contracts')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage Contracts (AMC/MMC)
            </button>
            <button 
              onClick={() => navigate('/licenses')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage Licenses
            </button>
            <button 
              onClick={() => navigate('/services')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Process service approvals
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard for Backoffice Accounts Executive - similar to BackOffice but with focus on accounts
const BackOfficeAccountsDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    customers: 0,
    leads: 0,
    newLeads: 0,
    services: 0,
    newServices: 0,
    pendingServices: 0,
    pendingApprovalServices: 0,
    newComplaints: 0,
    totalComplaints: 0,
    pendingPayments: 0,
    pendingPaymentsValue: 0,
    totalSales: 0,
    totalSalesValue: 0,
    expiringContracts: 0,
    expiringLicenses: 0
  });

  const fetchDashboardData = async () => {
    try {
      const [customersRes, leadsRes, contractsRes, licensesRes, servicesRes, complaintsRes, salesStatsRes, billsRes] = await Promise.all([
        api.get('/customers/'),
        api.get('/leads/'),
        api.get('/contracts/'),
        api.get('/licenses/'),
        api.get('/services/'),
        api.get('/complaints/stats'),
        api.get('/sales-billing/stats').catch(() => ({ data: { pending_payments_count: 0, pending_payments_total: 0, completed_payments_count: 0, completed_payments_total: 0 } })),
        api.get('/sales-billing/completed-payments').catch(() => ({ data: [] }))
      ]);

      const newLeadsCount = leadsRes.data.filter(l => l.lead_status === 'New').length;
      
      // Service stats
      const newServicesCount = servicesRes.data.filter(s => s.status === 'New').length;
      const pendingServicesCount = servicesRes.data.filter(s => s.status === 'New' || s.status === 'On Process').length;
      const pendingApprovalCount = servicesRes.data.filter(s => s.status === 'Closed' && s.approval_status === 'Pending').length;

      const today = new Date();
      const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      // AMC Contracts expiring in next 30 days
      const expiringContractsCount = contractsRes.data.filter(c => {
        if (c.contract_type !== 'AMC' || !c.end_date) return false;
        const endDate = new Date(c.end_date);
        return endDate <= thirtyDaysLater && endDate >= today && c.contract_status === 'Active';
      }).length;

      // Licenses expiring in next 30 days
      const expiringLicensesCount = licensesRes.data.filter(l => {
        const renewalDate = new Date(l.renewal_due_date);
        return renewalDate <= thirtyDaysLater && renewalDate >= today && l.status === 'Active';
      }).length;

      setStats({
        customers: customersRes.data.length,
        leads: leadsRes.data.length,
        newLeads: newLeadsCount,
        services: servicesRes.data.length,
        newServices: newServicesCount,
        pendingServices: pendingServicesCount,
        pendingApprovalServices: pendingApprovalCount,
        newComplaints: complaintsRes.data.new || 0,
        totalComplaints: (complaintsRes.data.new || 0) + (complaintsRes.data.in_progress || 0) + (complaintsRes.data.resolved || 0),
        pendingPayments: salesStatsRes.data.pending_payments_count || 0,
        pendingPaymentsValue: salesStatsRes.data.pending_payments_total || 0,
        totalSales: salesStatsRes.data.completed_payments_count || 0,
        totalSalesValue: salesStatsRes.data.completed_payments_total || 0,
        expiringContracts: expiringContractsCount,
        expiringLicenses: expiringLicensesCount
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/sales')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Payments</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.pendingPayments}</p>
                <p className="text-xs text-gray-500">₹{stats.pendingPaymentsValue.toLocaleString('en-IN')}</p>
              </div>
              <div className={`${stats.pendingPayments > 0 ? 'bg-red-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <CreditCard className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/sales')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Sales</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.totalSales}</p>
                <p className="text-xs text-gray-500">₹{stats.totalSalesValue.toLocaleString('en-IN')}</p>
              </div>
              <div className="bg-green-500 p-2 rounded-lg">
                <FileText className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/services')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pendingApprovalServices}</p>
                <p className="text-xs text-gray-500">Services to Approve</p>
              </div>
              <div className={`${stats.pendingApprovalServices > 0 ? 'bg-amber-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <FileCheck className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/customers')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Customers</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.customers}</p>
              </div>
              <div className="bg-blue-500 p-2 rounded-lg">
                <Building2 className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/leads')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Leads</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats.newLeads}</p>
                <p className="text-xs text-gray-500">{stats.leads} Total</p>
              </div>
              <div className="bg-orange-500 p-2 rounded-lg">
                <Phone className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/services')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Services</p>
                <p className="text-2xl font-bold text-cyan-600 mt-1">{stats.pendingServices}</p>
                <p className="text-xs text-gray-500">{stats.services} Total</p>
              </div>
              <div className={`${stats.pendingServices > 0 ? 'bg-cyan-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <Wrench className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/complaints')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Complaints</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.newComplaints}</p>
                <p className="text-xs text-gray-500">{stats.totalComplaints} Total</p>
              </div>
              <div className={`${stats.newComplaints > 0 ? 'bg-red-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <MessageSquareWarning className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate('/contracts')}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Expiring Contracts</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">{stats.expiringContracts}</p>
                <p className="text-xs text-gray-500">Next 30 days</p>
              </div>
              <div className={`${stats.expiringContracts > 0 ? 'bg-purple-500' : 'bg-gray-400'} p-2 rounded-lg`}>
                <Award className="text-white" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Your Responsibilities</CardTitle>
          <CardDescription>Accounts and service management activities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <button 
              onClick={() => navigate('/sales')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Process pending payments and manage sales
            </button>
            <button 
              onClick={() => navigate('/services')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Approve/reject closed services
            </button>
            <button 
              onClick={() => navigate('/customers')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage customer relationships
            </button>
            <button 
              onClick={() => navigate('/contracts')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage Contracts (AMC/MMC)
            </button>
            <button 
              onClick={() => navigate('/licenses')}
              className="w-full text-left text-sm text-gray-600 hover:text-blue-600 py-1"
            >
              • Manage Licenses / Stamping
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
